// src/components/AddProductoForm.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Button, Form, ListGroup } from "react-bootstrap";
import "animate.css";
import "../css/addlocalidadform.css";

import {
  postProducto as addProducto,
  getProductos,
} from "../helpers/rutaProductos";

import {
  puedeVerCostosFront,
} from "../helpers/permisosCostos";

import { getCategoriasProductos as getCategorias } from "../helpers/rutaCategoriasProductos";
import { getMarcas } from "../helpers/rutaMarcas";
import { getColores } from "../helpers/rutaColores";

/* ---------------- utils ---------------- */
const clean = (v) => String(v ?? "").trim();

const toIntOrZero = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const toNumberOrZero = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const upper = (v) => clean(v).toUpperCase();

/* ---------------- normalizadores ---------------- */
const normalizeProductos = (r) => {
  const arr = Array.isArray(r?.productos)
    ? r.productos
    : Array.isArray(r?.data)
    ? r.data
    : Array.isArray(r)
    ? r
    : [];

  return arr.map((p) => ({
    ...p,
    codprod:
      Number(
        p?.codprod ??
          p?.codproducto ??
          p?.codigo ??
          p?.cod ??
          p?.numero ??
          0
      ) || 0,
  }));
};

const normalizeCategorias = (r) => {
  const arr = Array.isArray(r?.categorias)
    ? r.categorias
    : Array.isArray(r?.data)
    ? r.data
    : Array.isArray(r)
    ? r
    : [];

  return arr.map((c) => ({
    ...c,
    codcategoria:
      Number(
        c?.codcategoria ??
          c?.codcat ??
          c?.codcateg ??
          c?.codigo ??
          c?.cod ??
          c?.numero ??
          0
      ) || 0,
  }));
};

const normalizeMarcas = (r) => {
  const arr = Array.isArray(r?.marcas)
    ? r.marcas
    : Array.isArray(r?.data)
    ? r.data
    : Array.isArray(r)
    ? r
    : [];

  return arr.map((m) => ({
    ...m,
    codmarca:
      Number(
        m?.codmarca ??
          m?.codmar ??
          m?.codigo ??
          m?.cod ??
          m?.numero ??
          0
      ) || 0,
  }));
};

const normalizeColores = (r) => {
  const arr = Array.isArray(r?.colores)
    ? r.colores
    : Array.isArray(r?.data)
    ? r.data
    : Array.isArray(r)
    ? r
    : [];

  return arr.map((c) => ({
    ...c,
    codcolor:
      Number(
        c?.codcolor ??
          c?.codigo ??
          c?.cod ??
          c?.numero ??
          0
      ) || 0,
  }));
};

/* ---------------- helpers categoría ---------------- */
const getCategoriaDocId = (c) =>
  clean(c?._id ?? c?.id ?? c?.categoriaId ?? c?.Categoria ?? c?.value ?? "");

const getCodCategoria = (c) => {
  const raw =
    c?.codcategoria ??
    c?.codcat ??
    c?.codcateg ??
    c?.codigo ??
    c?.cod ??
    c?.numero ??
    "";

  const n = Number(String(raw ?? "").trim());
  return Number.isFinite(n) ? n : 0;
};

const getDescCategoria = (c) =>
  clean(c?.categoria) ||
  clean(c?.descripcion) ||
  clean(c?.nombre) ||
  clean(c?.label) ||
  "";

/* ---------------- helpers marca ---------------- */
const getMarcaDocId = (m) =>
  clean(m?._id ?? m?.id ?? m?.marcaId ?? m?.Marca ?? m?.value ?? "");

const getCodMarca = (m) => {
  const raw =
    m?.codmarca ??
    m?.codmar ??
    m?.codigo ??
    m?.cod ??
    m?.numero ??
    "";

  const n = Number(String(raw ?? "").trim());
  return Number.isFinite(n) ? n : 0;
};

const getDescMarca = (m) =>
  clean(m?.marca) ||
  clean(m?.descripcion) ||
  clean(m?.nombre) ||
  clean(m?.label) ||
  "";

/* ---------------- helpers color ---------------- */
const getColorDocId = (c) =>
  clean(c?._id ?? c?.id ?? c?.colorId ?? c?.Color ?? c?.value ?? "");

const getCodColor = (c) => {
  const raw =
    c?.codcolor ??
    c?.codigo ??
    c?.cod ??
    c?.numero ??
    "";

  const n = Number(String(raw ?? "").trim());
  return Number.isFinite(n) ? n : 0;
};

const getDescColor = (c) =>
  clean(c?.color) ||
  clean(c?.descripcion) ||
  clean(c?.nombre) ||
  clean(c?.label) ||
  "";

/* ---------------- initial ---------------- */
const initial = {
  codprod: "",

  categoria: "",
  codcategoria: "",

  marca: "",
  codmarca: "",

  modelo: "",
  nombreComercial: "",
  descripcion: "",

  tipoMotor: "NO_APLICA",

  cilindradaCC: "",
  potenciaWatts: "",
  potenciaKw: "",
  tensionVolt: "",
  capacidadAh: "",
  capacidadKwh: "",
  cantidadPaneles: "",
  potenciaPanelWatts: "",

  usaColor: false,
  coloresDisponibles: [],

  precioVentaUSD: "",
  precioMinimoUSD: "",
  costoProductoUSD: "",
  comisionBaseUSD: "",
  costoEnvioUSD: "",

  politicaEnvio: "NO_APLICA",
  permiteEnvio: true,
  demoraEntrega: "",
  aclaracion: "",

  especificaciones: [],

  observaciones: "",
  activo: true,
};

export default function AddProductoForm({ setShow }) {
  const puedeVerCostos = puedeVerCostosFront();
  const [formValues, setFormValues] = useState(initial);
  const [showSuccess, setShowSuccess] = useState(false);

  const [ui, setUi] = useState({
    categoriaDesc: "",
    marcaDesc: "",
  });

  const [productosState, setProductosState] = useState({
    data: [],
    loading: true,
  });

  const [categoriasState, setCategoriasState] = useState({
    data: [],
    loading: true,
  });

  const [marcasState, setMarcasState] = useState({
    data: [],
    loading: true,
  });

  const [coloresState, setColoresState] = useState({
    data: [],
    loading: true,
  });

  const setField = useCallback((name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setField(name, value);
  };

  const handleCheckChange = (e) => {
    const { name, checked } = e.target;

    setFormValues((prev) => ({
      ...prev,
      [name]: checked,
      ...(name === "usaColor" && !checked ? { coloresDisponibles: [] } : {}),
    }));
  };

  const getNextCodProd = useCallback((arr = []) => {
    const max = arr.reduce((acc, item) => {
      const n = Number(item?.codprod ?? 0) || 0;
      return n > acc ? n : acc;
    }, 0);

    return String(max + 1);
  }, []);

  /* =========================================================
     CARGA INICIAL
  ========================================================= */
  useEffect(() => {
    getCategorias()
      .then((r) => {
        const data = normalizeCategorias(r);
        setCategoriasState({
          data,
          loading: false,
        });
      })
      .catch(() =>
        setCategoriasState({
          data: [],
          loading: false,
        })
      );
  }, []);

  useEffect(() => {
    getMarcas()
      .then((r) => {
        const data = normalizeMarcas(r);
        setMarcasState({
          data,
          loading: false,
        });
      })
      .catch(() =>
        setMarcasState({
          data: [],
          loading: false,
        })
      );
  }, []);

  useEffect(() => {
    getColores()
      .then((r) => {
        const data = normalizeColores(r);
        setColoresState({
          data,
          loading: false,
        });
      })
      .catch(() =>
        setColoresState({
          data: [],
          loading: false,
        })
      );
  }, []);

  useEffect(() => {
    let cancelado = false;

    const cargarProductos = async () => {
      try {
        const acumulados = [];
        let page = 1;

        while (true) {
          const respuesta = await getProductos({
            estado: "todos",
            limit: 200,
            page,
          });

          const lote = normalizeProductos(respuesta);
          acumulados.push(...lote);

          const total = Number(respuesta?.total);

          if (
            lote.length === 0 ||
            (Number.isFinite(total) && acumulados.length >= total)
          ) {
            break;
          }

          page += 1;
        }

        if (cancelado) return;

        setProductosState({
          data: acumulados,
          loading: false,
        });

        setFormValues((prev) => ({
          ...prev,
          codprod: getNextCodProd(acumulados),
        }));
      } catch (error) {
        if (cancelado) return;

        setProductosState({
          data: [],
          loading: false,
        });

        setFormValues((prev) => ({
          ...prev,
          codprod: "",
        }));

        alert(
          "No se pudo consultar la numeración de productos. Cerrá y volvé a abrir el formulario."
        );
      }
    };

    cargarProductos();

    return () => {
      cancelado = true;
    };
  }, [getNextCodProd]);

  /* =========================================================
     CATEGORÍAS
  ========================================================= */
  const [showCategoriaModal, setShowCategoriaModal] = useState(false);
  const [qCategoria, setQCategoria] = useState("");
  const [categoriaOpts, setCategoriaOpts] = useState([]);
  const [selIdxCategoria, setSelIdxCategoria] = useState(0);
  const inputCategoriaRef = useRef(null);

  const openCategoriaModal = useCallback(() => {
    setShowCategoriaModal(true);
    setQCategoria("");
    setCategoriaOpts([]);
    setSelIdxCategoria(0);
    setTimeout(() => inputCategoriaRef.current?.focus(), 50);
  }, []);

  const closeCategoriaModal = useCallback(() => {
    setShowCategoriaModal(false);
  }, []);

  const assignCategoria = useCallback(
    (cat) => {
      if (!cat) return;

      const id = getCategoriaDocId(cat);

      const codNum =
        getCodCategoria(cat) ||
        Number(
          categoriasState.data.find((x) => getCategoriaDocId(x) === id)
            ?.codcategoria ?? 0
        ) ||
        0;

      const desc = getDescCategoria(cat);

      setFormValues((prev) => ({
        ...prev,
        categoria: id,
        codcategoria: codNum > 0 ? String(codNum) : "",
      }));

      setUi((prev) => ({
        ...prev,
        categoriaDesc: desc,
      }));
    },
    [categoriasState.data]
  );

  const fetchCategoriaByCod = useCallback(
    async (cod) => {
      const num = Number(cod || 0);

      if (!Number.isFinite(num) || num <= 0) {
        setUi((prev) => ({ ...prev, categoriaDesc: "" }));
        setFormValues((prev) => ({ ...prev, categoria: "" }));
        return;
      }

      const base = Array.isArray(categoriasState.data)
        ? categoriasState.data
        : [];

      const pick =
        base.find((x) => Number(getCodCategoria(x)) === num) ||
        base.find((x) => String(getCodCategoria(x)) === String(num)) ||
        null;

      if (pick) {
        assignCategoria(pick);
      } else {
        setUi((prev) => ({ ...prev, categoriaDesc: "No encontrada" }));
        setFormValues((prev) => ({
          ...prev,
          categoria: "",
        }));
      }
    },
    [categoriasState.data, assignCategoria]
  );

  const buscarCategoria = useCallback(
    async (rawOverride) => {
      const raw = clean(rawOverride ?? formValues.codcategoria);

      if (raw === "" || raw === "0") {
        openCategoriaModal();
        return;
      }

      const cod = Number(raw);
      if (!Number.isFinite(cod) || cod <= 0) {
        openCategoriaModal();
        return;
      }

      await fetchCategoriaByCod(cod);
    },
    [formValues.codcategoria, openCategoriaModal, fetchCategoriaByCod]
  );

  useEffect(() => {
    if (!showCategoriaModal) return;

    const q = qCategoria.trim().toLowerCase();
    const base = Array.isArray(categoriasState.data)
      ? categoriasState.data
      : [];

    if (!q) {
      setCategoriaOpts(base.slice(0, 50));
      setSelIdxCategoria(0);
      return;
    }

    const filtered = base
      .filter((cat) => {
        const cod = String(getCodCategoria(cat));
        const desc = getDescCategoria(cat).toLowerCase();
        return cod.includes(q) || desc.includes(q);
      })
      .slice(0, 100);

    setCategoriaOpts(filtered);
    setSelIdxCategoria(0);
  }, [qCategoria, showCategoriaModal, categoriasState.data]);

  const seleccionarCategoria = useCallback(
    (cat) => {
      if (!cat) return;
      assignCategoria(cat);
      closeCategoriaModal();
    },
    [assignCategoria, closeCategoriaModal]
  );

  const onCategoriaModalKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeCategoriaModal();
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelIdxCategoria((i) =>
        Math.min(i + 1, Math.max(0, categoriaOpts.length - 1))
      );
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelIdxCategoria((i) => Math.max(i - 1, 0));
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      const pick = categoriaOpts[selIdxCategoria] || categoriaOpts[0];
      if (pick) seleccionarCategoria(pick);
    }
  };

  /* =========================================================
     MARCAS
  ========================================================= */
  const [showMarcaModal, setShowMarcaModal] = useState(false);
  const [qMarca, setQMarca] = useState("");
  const [marcaOpts, setMarcaOpts] = useState([]);
  const [selIdxMarca, setSelIdxMarca] = useState(0);
  const inputMarcaRef = useRef(null);

  const openMarcaModal = useCallback(() => {
    setShowMarcaModal(true);
    setQMarca("");
    setMarcaOpts([]);
    setSelIdxMarca(0);
    setTimeout(() => inputMarcaRef.current?.focus(), 50);
  }, []);

  const closeMarcaModal = useCallback(() => {
    setShowMarcaModal(false);
  }, []);

  const assignMarca = useCallback(
    (marca) => {
      if (!marca) return;

      const id = getMarcaDocId(marca);

      const codNum =
        getCodMarca(marca) ||
        Number(
          marcasState.data.find((x) => getMarcaDocId(x) === id)?.codmarca ?? 0
        ) ||
        0;

      const desc = getDescMarca(marca);

      setFormValues((prev) => ({
        ...prev,
        marca: id,
        codmarca: codNum > 0 ? String(codNum) : "",
      }));

      setUi((prev) => ({
        ...prev,
        marcaDesc: desc,
      }));
    },
    [marcasState.data]
  );

  const fetchMarcaByCod = useCallback(
    async (cod) => {
      const num = Number(cod || 0);

      if (!Number.isFinite(num) || num <= 0) {
        setUi((prev) => ({ ...prev, marcaDesc: "" }));
        setFormValues((prev) => ({ ...prev, marca: "" }));
        return;
      }

      const base = Array.isArray(marcasState.data) ? marcasState.data : [];

      const pick =
        base.find((x) => Number(getCodMarca(x)) === num) ||
        base.find((x) => String(getCodMarca(x)) === String(num)) ||
        null;

      if (pick) {
        assignMarca(pick);
      } else {
        setUi((prev) => ({ ...prev, marcaDesc: "No encontrada" }));
        setFormValues((prev) => ({
          ...prev,
          marca: "",
        }));
      }
    },
    [marcasState.data, assignMarca]
  );

  const buscarMarca = useCallback(
    async (rawOverride) => {
      const raw = clean(rawOverride ?? formValues.codmarca);

      if (raw === "" || raw === "0") {
        openMarcaModal();
        return;
      }

      const cod = Number(raw);
      if (!Number.isFinite(cod) || cod <= 0) {
        openMarcaModal();
        return;
      }

      await fetchMarcaByCod(cod);
    },
    [formValues.codmarca, openMarcaModal, fetchMarcaByCod]
  );

  useEffect(() => {
    if (!showMarcaModal) return;

    const q = qMarca.trim().toLowerCase();
    const base = Array.isArray(marcasState.data) ? marcasState.data : [];

    if (!q) {
      setMarcaOpts(base.slice(0, 50));
      setSelIdxMarca(0);
      return;
    }

    const filtered = base
      .filter((marca) => {
        const cod = String(getCodMarca(marca));
        const desc = getDescMarca(marca).toLowerCase();
        return cod.includes(q) || desc.includes(q);
      })
      .slice(0, 100);

    setMarcaOpts(filtered);
    setSelIdxMarca(0);
  }, [qMarca, showMarcaModal, marcasState.data]);

  const seleccionarMarca = useCallback(
    (marca) => {
      if (!marca) return;
      assignMarca(marca);
      closeMarcaModal();
    },
    [assignMarca, closeMarcaModal]
  );

  const onMarcaModalKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeMarcaModal();
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelIdxMarca((i) =>
        Math.min(i + 1, Math.max(0, marcaOpts.length - 1))
      );
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelIdxMarca((i) => Math.max(i - 1, 0));
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      const pick = marcaOpts[selIdxMarca] || marcaOpts[0];
      if (pick) seleccionarMarca(pick);
    }
  };

  /* =========================================================
     COLORES
  ========================================================= */
  const [showColorModal, setShowColorModal] = useState(false);
  const [qColor, setQColor] = useState("");
  const [colorOpts, setColorOpts] = useState([]);
  const inputColorRef = useRef(null);

  const openColorModal = useCallback(() => {
    setShowColorModal(true);
    setQColor("");
    setColorOpts([]);
    setTimeout(() => inputColorRef.current?.focus(), 50);
  }, []);

  const closeColorModal = useCallback(() => {
    setShowColorModal(false);
  }, []);

  useEffect(() => {
    if (!showColorModal) return;

    const q = qColor.trim().toLowerCase();
    const base = Array.isArray(coloresState.data) ? coloresState.data : [];

    if (!q) {
      setColorOpts(base.slice(0, 100));
      return;
    }

    const filtered = base
      .filter((color) => {
        const cod = String(getCodColor(color));
        const desc = getDescColor(color).toLowerCase();
        return cod.includes(q) || desc.includes(q);
      })
      .slice(0, 100);

    setColorOpts(filtered);
  }, [qColor, showColorModal, coloresState.data]);

  const toggleColor = (color) => {
    const id = getColorDocId(color);
    if (!id) return;

    setFormValues((prev) => {
      const actual = Array.isArray(prev.coloresDisponibles)
        ? prev.coloresDisponibles
        : [];

      const existe = actual.includes(id);

      return {
        ...prev,
        coloresDisponibles: existe
          ? actual.filter((x) => x !== id)
          : [...actual, id],
      };
    });
  };

  const getColorNameById = (id) => {
    const color = coloresState.data.find((c) => getColorDocId(c) === id);
    return getDescColor(color) || id;
  };

  /* =========================================================
     ESPECIFICACIONES
  ========================================================= */
  const [specNombre, setSpecNombre] = useState("");
  const [specValor, setSpecValor] = useState("");

  const addEspecificacion = () => {
    const nombre = upper(specNombre);
    const valor = upper(specValor);

    if (!nombre || !valor) {
      alert("Completá nombre y valor de la especificación.");
      return;
    }

    setFormValues((prev) => ({
      ...prev,
      especificaciones: [
        ...(Array.isArray(prev.especificaciones) ? prev.especificaciones : []),
        { nombre, valor },
      ],
    }));

    setSpecNombre("");
    setSpecValor("");
  };

  const removeEspecificacion = (idx) => {
    setFormValues((prev) => ({
      ...prev,
      especificaciones: prev.especificaciones.filter((_, i) => i !== idx),
    }));
  };

  /* ---------------- validate / payload ---------------- */
  const validate = () => {
    if (!toIntOrZero(formValues.codprod)) {
      return "No se pudo calcular el código de producto.";
    }

    if (!clean(formValues.categoria)) {
      return "Seleccioná una categoría válida.";
    }

    if (!clean(formValues.marca)) {
      return "Seleccioná una marca válida.";
    }

    if (!clean(formValues.modelo)) {
      return "Ingresá el modelo.";
    }

    if (!clean(formValues.nombreComercial)) {
      return "Ingresá el nombre comercial.";
    }

    if (!clean(formValues.descripcion)) {
      return "Ingresá la descripción.";
    }

    if (formValues.usaColor && !formValues.coloresDisponibles.length) {
      return "Marcaste que usa color, pero no seleccionaste colores.";
    }

    return "";
  };

  const buildPayload = () => {
    const payload = {
      codprod: toIntOrZero(formValues.codprod),

      categoria: clean(formValues.categoria) || null,
      marca: clean(formValues.marca) || null,

      modelo: upper(formValues.modelo),
      nombreComercial: upper(formValues.nombreComercial),
      descripcion: upper(formValues.descripcion),

      tipoMotor:
        upper(formValues.tipoMotor || "NO_APLICA") === "HIBRIDO"
          ? "HIBRIDA"
          : upper(formValues.tipoMotor || "NO_APLICA"),

      cilindradaCC: toNumberOrZero(formValues.cilindradaCC),
      potenciaWatts: toNumberOrZero(formValues.potenciaWatts),
      potenciaKw: toNumberOrZero(formValues.potenciaKw),
      tensionVolt: toNumberOrZero(formValues.tensionVolt),
      capacidadAh: toNumberOrZero(formValues.capacidadAh),
      capacidadKwh: toNumberOrZero(formValues.capacidadKwh),
      cantidadPaneles: toNumberOrZero(formValues.cantidadPaneles),
      potenciaPanelWatts: toNumberOrZero(formValues.potenciaPanelWatts),

      usaColor: !!formValues.usaColor,
      coloresDisponibles: formValues.usaColor
        ? formValues.coloresDisponibles
        : [],

      precioVentaUSD: toNumberOrZero(formValues.precioVentaUSD),
      precioMinimoUSD: toNumberOrZero(formValues.precioMinimoUSD),
      comisionBaseUSD: toNumberOrZero(formValues.comisionBaseUSD),
      costoEnvioUSD: toNumberOrZero(formValues.costoEnvioUSD),

      politicaEnvio: upper(formValues.politicaEnvio || "NO_APLICA"),
      permiteEnvio: !!formValues.permiteEnvio,
      demoraEntrega: upper(formValues.demoraEntrega),
      aclaracion: upper(formValues.aclaracion),

      especificaciones: Array.isArray(formValues.especificaciones)
        ? formValues.especificaciones.map((e) => ({
            nombre: upper(e.nombre),
            valor: upper(e.valor),
          }))
        : [],

    observaciones: upper(formValues.observaciones),
    activo: !!formValues.activo,
    };

    if (puedeVerCostos) {
      payload.costoProductoUSD =
        toNumberOrZero(
          formValues.costoProductoUSD
        );
    }

    return payload;
  };

  const resetForm = useCallback(
    (nextCod = "") => {
      setFormValues({
        ...initial,
        codprod: nextCod || getNextCodProd(productosState.data),
      });

      setUi({
        categoriaDesc: "",
        marcaDesc: "",
      });

      setSpecNombre("");
      setSpecValor("");
    },
    [getNextCodProd, productosState.data]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    const msg = validate();
    if (msg) return alert(msg);

    const payload = buildPayload();

    try {
      const respuesta = await addProducto(payload);

      // Usamos el código realmente asignado por el backend.
      const nuevoCod = Number(respuesta?.producto?.codprod);
      const nextCod = String(nuevoCod + 1);

      setProductosState((prev) => ({
        ...prev,
        data: [...prev.data, { codprod: nuevoCod }],
      }));

      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
        resetForm(nextCod);
        if (typeof setShow === "function") setShow(false);
      }, 900);
    } catch (error) {
      console.error(
        "❌ Error al crear producto:",
        error?.response?.data || error
      );

      const mensaje =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "No se pudo guardar el producto.";

      alert(`No se pudo guardar el producto.\n\n${mensaje}`);
    }
  };

  const loading =
    productosState.loading ||
    categoriasState.loading ||
    marcasState.loading ||
    coloresState.loading;

  return (
    <>
      {!loading && (
        <div className="container">
          <form onSubmit={handleSubmit} noValidate>
            <div className="form-row">
              <div className="form-group mt-3 col-sm-2">
                <label>Código</label>
                <input
                  type="number"
                  className="form-control"
                  name="codprod"
                  value={formValues.codprod}
                  readOnly
                  placeholder="Auto"
                />
              </div>

              <div className="form-group mt-3 col-sm-4">
                <label>Modelo</label>
                <input
                  type="text"
                  className="form-control"
                  name="modelo"
                  maxLength="80"
                  value={formValues.modelo}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group mt-3 col-sm-4">
                <label>Nombre Comercial</label>
                <input
                  type="text"
                  className="form-control"
                  name="nombreComercial"
                  maxLength="120"
                  value={formValues.nombreComercial}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group mt-3 col-sm-2">
                <label>Activo</label>
                <div className="form-control d-flex align-items-center">
                  <input
                    type="checkbox"
                    name="activo"
                    checked={formValues.activo}
                    onChange={handleCheckChange}
                    style={{ marginRight: 8 }}
                  />
                  Sí
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group mt-3 col-sm-2">
                <label>Cod. Categoría</label>
                <div className="input-group">
                  <input
                    type="number"
                    className="form-control"
                    name="codcategoria"
                    value={formValues.codcategoria}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        e.stopPropagation();
                        buscarCategoria(e.currentTarget.value);
                      }
                    }}
                    onBlur={(e) => {
                      const v = clean(e.currentTarget.value);
                      if (v === "0") buscarCategoria(v);
                      else if (v) fetchCategoriaByCod(v);
                    }}
                    min="0"
                  />

                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => buscarCategoria()}
                    title="Buscar categoría"
                    style={{
                      width: 44,
                      height: 38,
                      padding: 0,
                      fontSize: "1rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    🔎
                  </button>
                </div>

                <small className="text-muted">
                  <b>0</b> y Enter buscar
                </small>
              </div>

              <div className="form-group mt-3 col-sm-4">
                <label>Categoría</label>
                <input
                  className="form-control"
                  value={ui.categoriaDesc}
                  readOnly
                  placeholder="Se completa desde catálogo"
                />
              </div>

              <div className="form-group mt-3 col-sm-2">
                <label>Cod. Marca</label>
                <div className="input-group">
                  <input
                    type="number"
                    className="form-control"
                    name="codmarca"
                    value={formValues.codmarca}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        e.stopPropagation();
                        buscarMarca(e.currentTarget.value);
                      }
                    }}
                    onBlur={(e) => {
                      const v = clean(e.currentTarget.value);
                      if (v === "0") buscarMarca(v);
                      else if (v) fetchMarcaByCod(v);
                    }}
                    min="0"
                  />

                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => buscarMarca()}
                    title="Buscar marca"
                    style={{
                      width: 44,
                      height: 38,
                      padding: 0,
                      fontSize: "1rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    🔎
                  </button>
                </div>

                <small className="text-muted">
                  <b>0</b> y Enter buscar
                </small>
              </div>

              <div className="form-group mt-3 col-sm-4">
                <label>Marca</label>
                <input
                  className="form-control"
                  value={ui.marcaDesc}
                  readOnly
                  placeholder="Se completa desde catálogo"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group mt-3 col-sm-12">
                <label>Descripción</label>
                <textarea
                  className="form-control"
                  name="descripcion"
                  rows="2"
                  maxLength="300"
                  value={formValues.descripcion}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group mt-3 col-sm-3">
                <label>Tipo Motor</label>
                <select
                  className="form-control"
                  name="tipoMotor"
                  value={formValues.tipoMotor}
                  onChange={handleChange}
                >
                  <option value="NO_APLICA">NO APLICA</option>
                  <option value="COMBUSTION">COMBUSTIÓN</option>
                  <option value="ELECTRICA">ELECTRICA</option>
                  <option value="HIBRIDA">HÍBRIDO</option>
                </select>
              </div>

              <div className="form-group mt-3 col-sm-3">
                <label>Cilindrada CC</label>
                <input
                  type="number"
                  className="form-control"
                  name="cilindradaCC"
                  value={formValues.cilindradaCC}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group mt-3 col-sm-3">
                <label>Potencia Watts</label>
                <input
                  type="number"
                  className="form-control"
                  name="potenciaWatts"
                  value={formValues.potenciaWatts}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group mt-3 col-sm-3">
                <label>Potencia Kw</label>
                <input
                  type="number"
                  className="form-control"
                  name="potenciaKw"
                  value={formValues.potenciaKw}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group mt-3 col-sm-3">
                <label>Tensión Volt</label>
                <input
                  type="number"
                  className="form-control"
                  name="tensionVolt"
                  value={formValues.tensionVolt}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group mt-3 col-sm-3">
                <label>Capacidad Ah</label>
                <input
                  type="number"
                  className="form-control"
                  name="capacidadAh"
                  value={formValues.capacidadAh}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group mt-3 col-sm-3">
                <label>Capacidad Kwh</label>
                <input
                  type="number"
                  className="form-control"
                  name="capacidadKwh"
                  value={formValues.capacidadKwh}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group mt-3 col-sm-3">
                <label>Cant. Paneles</label>
                <input
                  type="number"
                  className="form-control"
                  name="cantidadPaneles"
                  value={formValues.cantidadPaneles}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group mt-3 col-sm-3">
                <label>Potencia Panel Watts</label>
                <input
                  type="number"
                  className="form-control"
                  name="potenciaPanelWatts"
                  value={formValues.potenciaPanelWatts}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group mt-3 col-sm-3">
                <label>Precio Venta USD</label>
                <input
                  type="number"
                  className="form-control"
                  name="precioVentaUSD"
                  value={formValues.precioVentaUSD}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group mt-3 col-sm-3">
                <label>Precio Mínimo USD</label>
                <input
                  type="number"
                  className="form-control"
                  name="precioMinimoUSD"
                  value={formValues.precioMinimoUSD}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group mt-3 col-sm-3">
                <label>Comisión Base USD</label>
                <input
                  type="number"
                  className="form-control"
                  name="comisionBaseUSD"
                  value={formValues.comisionBaseUSD}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              {puedeVerCostos && (
                <div className="form-group mt-3 col-sm-3">
                  <label>Costo Producto USD</label>

                  <input
                    type="number"
                    className="form-control"
                    name="costoProductoUSD"
                    value={formValues.costoProductoUSD}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
              )}

              <div className="form-group mt-3 col-sm-3">
                <label>Costo Envío USD</label>

                <input
                  type="number"
                  className="form-control"
                  name="costoEnvioUSD"
                  value={formValues.costoEnvioUSD}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group mt-3 col-sm-3">
                <label>Política Envío</label>

                <select
                  className="form-control"
                  name="politicaEnvio"
                  value={formValues.politicaEnvio}
                  onChange={handleChange}
                >
                  <option value="NO_APLICA">NO APLICA</option>
                  <option value="INCLUIDO">INCLUIDO</option>
                  <option value="A_CARGO_CLIENTE">
                    A CARGO CLIENTE
                  </option>
                  <option value="A_COORDINAR">
                    A COORDINAR
                  </option>
                </select>
              </div>

              <div className="form-group mt-3 col-sm-3">
                <label>Permite Envío</label>

                <div className="form-control d-flex align-items-center">
                  <input
                    type="checkbox"
                    name="permiteEnvio"
                    checked={formValues.permiteEnvio}
                    onChange={handleCheckChange}
                    style={{ marginRight: 8 }}
                  />
                  Sí
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group mt-3 col-sm-3">
                <label>Demora Entrega</label>

                <input
                  type="text"
                  className="form-control"
                  name="demoraEntrega"
                  value={formValues.demoraEntrega}
                  onChange={handleChange}
                  placeholder="Ej: 15 DÍAS"
                />
              </div>

              <div className="form-group mt-3 col-sm-3">
                <label>Usa Color</label>

                <div className="form-control d-flex align-items-center">
                  <input
                    type="checkbox"
                    name="usaColor"
                    checked={formValues.usaColor}
                    onChange={handleCheckChange}
                    style={{ marginRight: 8 }}
                  />
                  Sí
                </div>
              </div>

              <div className="form-group mt-3 col-sm-6">
                <label>Colores Disponibles</label>

                <div className="input-group">
                  <input
                    className="form-control"
                    readOnly
                    value={
                      formValues.coloresDisponibles.length
                        ? formValues.coloresDisponibles
                            .map((id) => getColorNameById(id))
                            .join(", ")
                        : ""
                    }
                    placeholder={
                      formValues.usaColor
                        ? "Seleccioná colores"
                        : "No aplica"
                    }
                  />

                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={openColorModal}
                    disabled={!formValues.usaColor}
                  >
                    🔎 Colores
                  </button>
                </div>
              </div>
            </div>




            <div className="form-row">
              <div className="form-group mt-3 col-sm-4">
                <label>Especificación</label>
                <input
                  type="text"
                  className="form-control"
                  value={specNombre}
                  onChange={(e) => setSpecNombre(e.target.value)}
                  placeholder="Ej: BATERÍA"
                />
              </div>

              <div className="form-group mt-3 col-sm-4">
                <label>Valor</label>
                <input
                  type="text"
                  className="form-control"
                  value={specValor}
                  onChange={(e) => setSpecValor(e.target.value)}
                  placeholder="Ej: 72V"
                />
              </div>

              <div className="form-group mt-3 col-sm-4 d-flex align-items-end">
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={addEspecificacion}
                >
                  Agregar Especificación
                </button>
              </div>
            </div>

            {!!formValues.especificaciones.length && (
              <div className="mt-3">
                <ListGroup>
                  {formValues.especificaciones.map((e, idx) => (
                    <ListGroup.Item
                      key={`${e.nombre}-${idx}`}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <span>
                        <b>{e.nombre}</b>: {e.valor}
                      </span>

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeEspecificacion(idx)}
                      >
                        Quitar
                      </button>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            )}

            <div className="form-row">
              <div className="form-group mt-3 col-sm-6">
                <label>Aclaración</label>
                <textarea
                  className="form-control"
                  name="aclaracion"
                  rows="2"
                  value={formValues.aclaracion}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group mt-3 col-sm-6">
                <label>Observaciones</label>
                <textarea
                  className="form-control"
                  name="observaciones"
                  rows="2"
                  value={formValues.observaciones}
                  onChange={handleChange}
                />
              </div>
            </div>

            <button type="submit" className="btn btn-dark mt-5 mb-3" id="button">
              Guardar
            </button>
          </form>
        </div>
      )}

      {/* MODAL CATEGORÍA */}
      <Modal
        show={showCategoriaModal}
        onHide={closeCategoriaModal}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Buscar categoría</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label>Código / Categoría</Form.Label>
            <Form.Control
              ref={inputCategoriaRef}
              value={qCategoria}
              placeholder="Ej: moto o 1"
              onChange={(e) => setQCategoria(e.target.value)}
              onKeyDown={onCategoriaModalKeyDown}
            />
            <Form.Text className="text-muted">
              ↑ ↓ para moverte • Enter selecciona • Esc cierra
            </Form.Text>
          </Form.Group>

          <div className="mt-3" style={{ maxHeight: 320, overflowY: "auto" }}>
            {!categoriaOpts.length && (
              <div className="text-muted">
                {qCategoria.trim()
                  ? "Sin resultados."
                  : "Escribí para buscar o elegí de la lista."}
              </div>
            )}

            {!!categoriaOpts.length && (
              <ListGroup>
                {categoriaOpts.map((cat, idx) => {
                  const cod = getCodCategoria(cat);
                  const desc = getDescCategoria(cat);

                  return (
                    <ListGroup.Item
                      key={getCategoriaDocId(cat) || `${desc}-${idx}`}
                      action
                      active={idx === selIdxCategoria}
                      onClick={() => seleccionarCategoria(cat)}
                    >
                      {cod > 0 ? <b>{cod} — </b> : null}
                      {desc}
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeCategoriaModal}>
            Cerrar
          </Button>

          <Button
            variant="primary"
            onClick={() =>
              seleccionarCategoria(
                categoriaOpts[selIdxCategoria] || categoriaOpts[0]
              )
            }
            disabled={!categoriaOpts.length}
          >
            Seleccionar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL MARCA */}
      <Modal
        show={showMarcaModal}
        onHide={closeMarcaModal}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Buscar marca</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label>Código / Marca</Form.Label>
            <Form.Control
              ref={inputMarcaRef}
              value={qMarca}
              placeholder="Ej: honda o 1"
              onChange={(e) => setQMarca(e.target.value)}
              onKeyDown={onMarcaModalKeyDown}
            />
            <Form.Text className="text-muted">
              ↑ ↓ para moverte • Enter selecciona • Esc cierra
            </Form.Text>
          </Form.Group>

          <div className="mt-3" style={{ maxHeight: 320, overflowY: "auto" }}>
            {!marcaOpts.length && (
              <div className="text-muted">
                {qMarca.trim()
                  ? "Sin resultados."
                  : "Escribí para buscar o elegí de la lista."}
              </div>
            )}

            {!!marcaOpts.length && (
              <ListGroup>
                {marcaOpts.map((marca, idx) => {
                  const cod = getCodMarca(marca);
                  const desc = getDescMarca(marca);

                  return (
                    <ListGroup.Item
                      key={getMarcaDocId(marca) || `${desc}-${idx}`}
                      action
                      active={idx === selIdxMarca}
                      onClick={() => seleccionarMarca(marca)}
                    >
                      {cod > 0 ? <b>{cod} — </b> : null}
                      {desc}
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeMarcaModal}>
            Cerrar
          </Button>

          <Button
            variant="primary"
            onClick={() =>
              seleccionarMarca(marcaOpts[selIdxMarca] || marcaOpts[0])
            }
            disabled={!marcaOpts.length}
          >
            Seleccionar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL COLORES */}
      <Modal
        show={showColorModal}
        onHide={closeColorModal}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Seleccionar colores</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label>Código / Color</Form.Label>
            <Form.Control
              ref={inputColorRef}
              value={qColor}
              placeholder="Ej: rojo o 1"
              onChange={(e) => setQColor(e.target.value)}
            />
          </Form.Group>

          <div className="mt-3" style={{ maxHeight: 320, overflowY: "auto" }}>
            {!colorOpts.length && (
              <div className="text-muted">
                {qColor.trim()
                  ? "Sin resultados."
                  : "Escribí para buscar o elegí de la lista."}
              </div>
            )}

            {!!colorOpts.length && (
              <ListGroup>
                {colorOpts.map((color, idx) => {
                  const id = getColorDocId(color);
                  const cod = getCodColor(color);
                  const desc = getDescColor(color);
                  const checked = formValues.coloresDisponibles.includes(id);

                  return (
                    <ListGroup.Item
                      key={id || `${desc}-${idx}`}
                      action
                      onClick={() => toggleColor(color)}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        readOnly
                        style={{ marginRight: 8 }}
                      />
                      {cod > 0 ? <b>{cod} — </b> : null}
                      {desc}
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeColorModal}>
            Cerrar
          </Button>

          <Button variant="primary" onClick={closeColorModal}>
            Aceptar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL SUCCESS */}
      <Modal
        show={showSuccess}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="animate__animated animate__fadeInDown"
      >
        <Modal.Body className="text-center">
          <div style={{ fontSize: "3rem" }}>🖐</div>
          <h5 className="mt-2">¡Producto generado correctamente!</h5>
        </Modal.Body>
      </Modal>
    </>
  );
}